import { useMemo, useState } from "react";
import { useSelectedNavTreeNode } from "@/components/Canvas/stores/canvasStore";
import {
  type ClassTypeWithPropertyType,
  checkArbitraryTailwindClass,
  detectClassPropertyType,
  PropertyType,
  parseTailwindColorWithOpacity,
  safeExtractValueFromCss,
} from "@/tailwind/tailwindUtils";
import { useActiveCanvas } from "@/utils/utils";
import type { CssFieldValue } from ".";

const getActualComputedValueForProperty = (
  propertyType: PropertyType,
  cssValues: { [cssName: string]: CssFieldValue },
): CssFieldValue | undefined => {
  switch (propertyType) {
    case PropertyType.WIDTH:
      return cssValues["width"];
    case PropertyType.MIN_WIDTH:
      return cssValues["min-width"];
    case PropertyType.MAX_WIDTH:
      return cssValues["max-width"];
    case PropertyType.HEIGHT:
      return cssValues["height"];
    case PropertyType.MIN_HEIGHT:
      return cssValues["min-height"];
    case PropertyType.MAX_HEIGHT:
      return cssValues["max-height"];
    case PropertyType.ROTATE:
      if (
        cssValues["rotate"] &&
        cssValues["rotate"].evaluatedValue !== "none"
      ) {
        return cssValues["rotate"];
      }
      if (cssValues["transform"]) {
        const match = cssValues["transform"].evaluatedValue?.match(
          /matrix\(([^),]+),([^),]+),([^),]+),([^),]+),([^),]+),([^),]+)\)/,
        );
        if (match) {
          const [, a, b] = match;
          const angle =
            Math.round(
              Math.atan2(
                Number.parseFloat(b.trim()),
                Number.parseFloat(a.trim()),
              ) *
                (180 / Math.PI) *
                1000,
            ) / 1000;
          if (!Number.isNaN(angle)) {
            return {
              cssName: "rotate",
              evaluatedValue: `${angle.toString()}deg`,
            };
          }
        }
      }
      return {
        cssName: "rotate",
        evaluatedValue: "0deg",
      };
    case PropertyType.BORDER_RADIUS:
      return cssValues["border-radius"];
    case PropertyType.BORDER_STYLE:
      return cssValues["border-style"];
    case PropertyType.BORDER_COLOR:
      return cssValues["border-color"];
    case PropertyType.BORDER_WIDTH:
      return cssValues["border-width"];
    case PropertyType.BORDER_TOP_COLOR:
      return cssValues["border-top-color"];
    case PropertyType.BORDER_TOP_WIDTH:
      return cssValues["border-top-width"];
    case PropertyType.BORDER_BOTTOM_COLOR:
      return cssValues["border-bottom-color"];
    case PropertyType.BORDER_BOTTOM_WIDTH:
      return cssValues["border-bottom-width"];
    case PropertyType.BORDER_LEFT_COLOR:
      return cssValues["border-left-color"];
    case PropertyType.BORDER_LEFT_WIDTH:
      return cssValues["border-left-width"];
    case PropertyType.BORDER_RIGHT_COLOR:
      return cssValues["border-right-color"];
    case PropertyType.BORDER_RIGHT_WIDTH:
      return cssValues["border-right-width"];
    case PropertyType.BORDER_TOP_LEFT_RADIUS:
      return cssValues["border-top-left-radius"];
    case PropertyType.BORDER_TOP_RIGHT_RADIUS:
      return cssValues["border-top-right-radius"];
    case PropertyType.BORDER_BOTTOM_RIGHT_RADIUS:
      return cssValues["border-bottom-right-radius"];
    case PropertyType.BORDER_BOTTOM_LEFT_RADIUS:
      return cssValues["border-bottom-left-radius"];
    case PropertyType.FONT_WEIGHT:
      return cssValues["font-weight"];
    case PropertyType.FONT_SIZE:
      return cssValues["font-size"];
    case PropertyType.LINE_HEIGHT:
      return cssValues["line-height"];
    case PropertyType.LETTER_SPACING:
      return cssValues["letter-spacing"];
    case PropertyType.PADDING:
      return cssValues["padding"];
    case PropertyType.PADDING_VERTICAL:
      return cssValues["padding-top"] || cssValues["padding-bottom"];
    case PropertyType.PADDING_HORIZONTAL:
      return cssValues["padding-left"] || cssValues["padding-right"];
    case PropertyType.PADDING_TOP:
      return cssValues["padding-top"];
    case PropertyType.PADDING_BOTTOM:
      return cssValues["padding-bottom"];
    case PropertyType.PADDING_LEFT:
      return cssValues["padding-left"];
    case PropertyType.PADDING_RIGHT:
      return cssValues["padding-right"];
    case PropertyType.MARGIN:
      return cssValues["margin"];
    case PropertyType.MARGIN_VERTICAL:
      return cssValues["margin-top"] || cssValues["margin-bottom"];
    case PropertyType.MARGIN_HORIZONTAL:
      return cssValues["margin-left"] || cssValues["margin-right"];
    case PropertyType.MARGIN_TOP:
      return cssValues["margin-top"];
    case PropertyType.MARGIN_BOTTOM:
      return cssValues["margin-bottom"];
    case PropertyType.MARGIN_LEFT:
      return cssValues["margin-left"];
    case PropertyType.MARGIN_RIGHT:
      return cssValues["margin-right"];
    case PropertyType.COLUMN_GAP:
      return cssValues["column-gap"];
    case PropertyType.ROW_GAP:
      return cssValues["row-gap"];
    case PropertyType.ANIMATION:
      return cssValues["animation"];
    case PropertyType.BOX_SHADOW:
      return cssValues["box-shadow"];
    case PropertyType.OPACITY:
      return cssValues["opacity"];
    case PropertyType.TEXT_COLOR:
      return cssValues["color"];
    case PropertyType.BACKGROUND_COLOR:
      return cssValues["background-color"];
    case PropertyType.JUSTIFY_CONTENT:
      return cssValues["justify-content"];
    case PropertyType.ALIGN_ITEMS:
      return cssValues["align-items"];
    case PropertyType.ALIGN_CONTENT:
      return cssValues["align-content"];
    case PropertyType.FONT_FAMILY:
      return cssValues["font-family"];
    case PropertyType.FONT_STYLE:
      return cssValues["font-style"];
    case PropertyType.TEXT_DECORATION_LINE:
      return cssValues["text-decoration-line"];
    case PropertyType.LEFT:
      return cssValues["left"];
    case PropertyType.TOP:
      return cssValues["top"];
    case PropertyType.RIGHT:
      return cssValues["right"];
    case PropertyType.BOTTOM:
      return cssValues["bottom"];
    case PropertyType.POSITION:
      return cssValues["position"];
    case PropertyType.BACKGROUND_IMAGE:
      return cssValues["background-image"];
    case PropertyType.TEXT_ALIGN:
      return cssValues["text-align"];
    case PropertyType.DISPLAY:
      return cssValues["display"];
    case PropertyType.FLEX_DIRECTION:
      return cssValues["flex-direction"];
    case PropertyType.BLUR:
      return cssValues["--tw-blur"];
  }
};

export const classMatchedPropertyType = ({
  className,
  propertyType,
  tailwindClassMap,
  variant,
}: {
  className: string;
  propertyType: PropertyType;
  tailwindClassMap: Record<string, ClassTypeWithPropertyType>;
  variant?: string | null;
}): {
  className: string;
  propertyValue: string;
  displayValue: string | null;
} | null => {
  let actualClassName = className;
  if (variant && variant !== "default") {
    if (!className.includes(":")) {
      return null;
    }

    const v = className.split(":")[0];
    if (v !== variant) {
      return null;
    }

    actualClassName = className.split(":")[1];
  } else {
    if (className.includes(":")) {
      return null;
    }
  }

  const arbitraryTailwindClass = checkArbitraryTailwindClass(
    className,
    propertyType,
  );

  const matchedTailwindClass = tailwindClassMap[actualClassName];

  if (matchedTailwindClass) {
    if (matchedTailwindClass.propertyType === propertyType) {
      let displayValue = arbitraryTailwindClass?.propertyValue || null;
      if (
        propertyType === PropertyType.BACKGROUND_COLOR ||
        propertyType === PropertyType.BORDER_COLOR ||
        propertyType === PropertyType.TEXT_COLOR
      ) {
        // For colors, if it's a Tailwind class (not arbitrary), use the class name as display value
        if (!arbitraryTailwindClass) {
          displayValue = actualClassName;
        } else {
          const colorToParse =
            arbitraryTailwindClass?.propertyValue ?? displayValue ?? "";
          const parsed = parseTailwindColorWithOpacity(colorToParse);
          if (parsed) {
            displayValue = parsed;
          } else {
            displayValue = safeExtractValueFromCss(matchedTailwindClass);
          }
        }
      }
      return {
        className: className,
        propertyValue: safeExtractValueFromCss(matchedTailwindClass),
        displayValue,
      };
    }
  }

  if (arbitraryTailwindClass) {
    let displayValue = arbitraryTailwindClass.propertyValue;

    // Special handling for color properties with CSS custom properties
    if (
      (propertyType === PropertyType.BACKGROUND_COLOR ||
        propertyType === PropertyType.BORDER_COLOR ||
        propertyType === PropertyType.TEXT_COLOR) &&
      arbitraryTailwindClass.propertyValue.includes("var(--")
    ) {
      // Extract the custom property name from var(--property-name)
      const varMatch =
        arbitraryTailwindClass.propertyValue.match(/var\(--([^)]+)\)/);
      if (varMatch) {
        const propertyName = varMatch[1];
        // Use just the property name as display value (e.g., "accent" instead of "hsl(var(--accent))")
        displayValue = propertyName;
      }
    }

    return {
      className: className,
      propertyValue: arbitraryTailwindClass.propertyValue,
      displayValue,
    };
  }

  // Fallback: For color properties, also match by class name pattern even if not in tailwindClassMap
  // This handles custom colors that might not be in the official Tailwind config
  // TODO: add border and text color prefixes later but be careful not to strip text size when changing text color
  if (propertyType === PropertyType.BACKGROUND_COLOR) {
    const colorPrefixes = {
      [PropertyType.BACKGROUND_COLOR]: "bg-",
    };

    const prefix = colorPrefixes[propertyType];
    if (actualClassName.startsWith(prefix)) {
      // This is likely a color class even if not in the map
      return {
        className: className,
        propertyValue: actualClassName,
        displayValue: actualClassName,
      };
    }
  }

  return null;
};

export const useExistingVariants = ({
  propertyType,
  elementClasses,
  tailwindClassMap,
}: {
  propertyType: PropertyType;
  elementClasses: string[];
  tailwindClassMap: Record<string, ClassTypeWithPropertyType>;
}) => {
  const variants: string[] = [];
  for (const className of elementClasses) {
    if (className.includes(":")) {
      const [variant, actualClassName] = className.split(":");
      const result = classMatchedPropertyType({
        className: actualClassName,
        propertyType,
        tailwindClassMap,
      });
      if (result) {
        if (!variants.includes(variant)) {
          variants.push(variant);
        }
      }
    }
  }

  return variants;
};

export const isClassVariant = (className: string) => {
  const splitted = className.split(":");
  if (splitted.length === 2) {
    return true;
  }

  return false;
};

interface NewPropertyStateProps {
  propertyType: PropertyType;
  elementClasses: string[];
  tailwindClassMap: Record<string, ClassTypeWithPropertyType>;
  cssValues: { [cssName: string]: CssFieldValue };
  variant?: string | null;
}

export interface ValueByVariant {
  [variant: string]: {
    value: string;
    active: boolean;
    displayValue: string | null;
  };
}

export interface NewPropertyState {
  className: string;

  // This is the value as defined by tailwind (e.g. 3rem)
  propertyValue: string;

  // This is the browser/dom value (e.g. 48px)
  cssValue: string;

  // This is the value to display, priority is the tailwind class, then the property value if not that
  displayValue: string;

  isSet: boolean;
  temporaryDraggingValue: string | null;
  setTemporaryDraggingValue: (value: string | null) => void;
  valueByVariant: ValueByVariant;
  propertyType: PropertyType;
}

const VARIANT_TO_MIN_SIZE = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

export const useNewPropertyState2 = ({
  propertyType,
  elementClasses,
  tailwindClassMap,
  cssValues,
  variant,
}: NewPropertyStateProps): NewPropertyState => {
  const [temporaryDraggingValue, setTemporaryDraggingValue] = useState<
    string | null
  >(null);

  const { selectedElement } = useSelectedNavTreeNode();
  const activeCanvas = useActiveCanvas();
  const selectedStoryboard = activeCanvas?.storyboards.find(
    (sb) => selectedElement?.storyboardId === sb.id,
  );

  return useMemo((): NewPropertyState => {
    const valueByVariant: Record<
      string,
      {
        value: string;
        active: boolean;
        displayValue: string | null;
      }
    > = {};

    const temporaryDraggingValueSet =
      temporaryDraggingValue !== null && temporaryDraggingValue !== undefined;

    const actualComputedValue = getActualComputedValueForProperty(
      propertyType,
      cssValues,
    );

    let result: {
      className: string;
      propertyValue: string;
      displayValue: string | null;
    } | null = null;

    for (const className of elementClasses) {
      if (isClassVariant(className)) {
        const [variant, actualClassName] = className.split(":");

        // For arbitrary classes, check if this class actually matches the property type
        // This prevents border-[34.25px] (width) from being included in border-color state
        if (actualClassName.includes("[")) {
          const detectedType = detectClassPropertyType(actualClassName);
          // If it's detected as a different property type, skip it
          if (detectedType !== null && detectedType !== propertyType) {
            continue;
          }
        }

        const variantResult = classMatchedPropertyType({
          className: actualClassName,
          propertyType,
          tailwindClassMap,
        });
        if (variantResult) {
          valueByVariant[variant] = {
            value: variantResult.propertyValue,
            displayValue: variantResult.displayValue,
            active: true,
          };
        }
      } else {
        if (!result) {
          // Apply filtering for non-variant classes to prevent wrong property types
          if (propertyType === PropertyType.BORDER_COLOR) {
            // For border colors, only allow classes that start with 'border-' or are the generic 'border' class
            if (className !== "border" && !className.startsWith("border-")) {
              continue;
            }
          }

          // For arbitrary classes, use detectClassPropertyType
          if (className.includes("[")) {
            const detectedType = detectClassPropertyType(className);
            // If it's detected as a different property type, skip it
            if (detectedType !== null && detectedType !== propertyType) {
              continue;
            }
          }

          result = classMatchedPropertyType({
            className,
            propertyType,
            tailwindClassMap,
          });
        }
      }
    }

    if (result) {
      valueByVariant.default = {
        value: result.propertyValue,
        displayValue: result.displayValue,
        active: true,
      };
    }

    // Actually figure out which variant is active, filter for all active
    // and pick the largest one
    const activeVariant = Object.keys(valueByVariant)
      .sort((a, b) => {
        if (a === "default") {
          return 1;
        }

        if (b === "default") {
          return -1;
        }

        return VARIANT_TO_MIN_SIZE[b] - VARIANT_TO_MIN_SIZE[a];
      })
      .filter((variant) => {
        if (variant === "default") {
          return true;
        }

        return (
          VARIANT_TO_MIN_SIZE[variant] <= (selectedStoryboard?.width || 100_000)
        );
      })[0];

    // Set all others to inactive
    for (const variant of Object.keys(valueByVariant)) {
      valueByVariant[variant].active = variant === activeVariant;
    }

    if (result || activeVariant) {
      let propValue = result?.propertyValue;
      if (temporaryDraggingValueSet) {
        propValue = temporaryDraggingValue;
      } else if (activeVariant) {
        propValue = valueByVariant[activeVariant].value;
      }

      let displayValue = result?.displayValue || result?.propertyValue;
      if (temporaryDraggingValueSet) {
        displayValue = temporaryDraggingValue;
      } else if (activeVariant) {
        displayValue =
          valueByVariant[activeVariant].displayValue ||
          valueByVariant[activeVariant].value;
      }

      const finalState = {
        className: result?.className || "",
        propertyValue: propValue || "",
        displayValue: transformDisplayValueForWidthAndHeight(
          propertyType,
          displayValue || "",
        ),
        cssValue: actualComputedValue?.evaluatedValue || "",
        isSet: true,
        temporaryDraggingValue,
        setTemporaryDraggingValue,
        valueByVariant,
        propertyType,
      };

      return finalState;
    }

    let propValue = actualComputedValue?.evaluatedValue || "";
    if (temporaryDraggingValueSet) {
      propValue = temporaryDraggingValue;
    }

    return {
      className: "",
      propertyValue: propValue,
      cssValue: actualComputedValue?.evaluatedValue || "",
      isSet: false,
      displayValue: transformDisplayValueForWidthAndHeight(
        propertyType,
        propValue,
      ),
      temporaryDraggingValue,
      setTemporaryDraggingValue,
      valueByVariant,
      propertyType,
    };
  }, [
    propertyType,
    elementClasses,
    tailwindClassMap,
    cssValues,
    temporaryDraggingValue,
    selectedStoryboard,
  ]);
};

const transformDisplayValueForWidthAndHeight = (
  propertyType: PropertyType,
  displayValue: string,
) => {
  if (
    propertyType === PropertyType.WIDTH ||
    propertyType === PropertyType.HEIGHT
  ) {
    if (displayValue === "fit") return "Hug";
    else if (displayValue === "full") return "Fill";
  }
  return displayValue;
};
